import {
  Button,
  Divider,
  EButtonColorVariant,
  EHeadingStyleVariant,
  ELinkButtonVariant,
  ETextStyleVariant,
  Heading,
  LinkButton,
  Text,
  TextInput,
} from '@outdoorsyco/bonfire';
import React, { useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { emailValidator } from '@/constants/validationRules';
import { TAuthError } from '@/redux/modules/auth';

import { TFieldErrors } from '../types/TFieldErrors';

export interface ForgotPasswordFormFields {
  email: string;
}

export interface ForgotPasswordFormProps {
  error?: TAuthError | null;
  hideHeader?: boolean;
  loading?: boolean;
  loginRedirect?: string;
  onLogin?: () => void;
  onSubmit?: (formData: ForgotPasswordFormFields) => void;
  className?: string;
}

const ForgotPasswordForm: React.FC<ForgotPasswordFormProps> = props => {
  const [submitted, setSubmitted] = useState(false);
  const intl = useIntl();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormFields>();
  const onSubmit = async (formValues: ForgotPasswordFormFields) => {
    setSubmitted(true);

    if (props.onSubmit) {
      props.onSubmit(formValues);
    }
  };

  const headTitle = intl.formatMessage({
    defaultMessage: 'Reset your password',
    id: 'p+kmJg',
  });
  const email = intl.formatMessage({ defaultMessage: 'Email address', id: 'hJZwTS' });
  const fieldErrors: TFieldErrors<keyof ForgotPasswordFormFields> = {
    email: {
      required: intl.formatMessage({
        defaultMessage: 'Email is required',
        id: 'cU/aSG',
      }),
      pattern: intl.formatMessage({
        defaultMessage: 'Please enter a valid email address.',
        id: 'xs/MJu',
      }),
    },
  };
  const resetBtnLabel = intl.formatMessage({ defaultMessage: 'Reset Password', id: 'xl27nc' });
  const remeberText = intl.formatMessage({
    defaultMessage: 'Remember your password? Login',
    id: 'wkX9Rk',
  });
  const submittedText = intl.formatMessage({
    defaultMessage: 'We’ve emailed you instructions on how to reset your password.',
    id: 'Sd+EoA',
  });
  const emailErr = errors.email && fieldErrors.email[errors.email.type];

  const handleLoginClick = () => {
    if (props.onLogin) {
      props.onLogin();
    }
  };

  return (
    <section className={`flex flex-col ${props.className || ''}`}>
      {!props.hideHeader && (
        <Heading variant={EHeadingStyleVariant.H5} className="mb-4">
          {headTitle}
        </Heading>
      )}
      {submitted && !props.loading && !props.error ? (
        <Text variant={ETextStyleVariant.MediumRegular} className="flex flex-col justify-center">
          {submittedText}
        </Text>
      ) : (
        <form
          className="flex flex-col justify-center mt-2 mb-2 gap-4"
          onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name="email"
            control={control}
            rules={emailValidator}
            render={({ field }) => (
              <TextInput
                controlled
                name="email"
                autoComplete="email"
                label={email}
                ariaLabel={email}
                inputValue={field.value || ''}
                onChange={field.onChange}
                error={!!emailErr}
                helpText={emailErr}
              />
            )}
          />
          <Button
            type="submit"
            variant={EButtonColorVariant.Primary}
            label={resetBtnLabel}
            loading={props.loading}
          />
        </form>
      )}
      <Divider className="my-6" />
      <footer className="flex items-center justify-center border-gray-200">
        <LinkButton
          variant={ELinkButtonVariant.Underline}
          label={remeberText}
          onClick={handleLoginClick}
          href={props.loginRedirect}
        />
      </footer>
    </section>
  );
};

export default ForgotPasswordForm;
